<template>
    <div
        class="flex flex-col items-start"
        :data-testid="`${testidPrefix}-feedback-container`"
        @click.stop
        @mousedown.stop
    >
        <div
            class="mt-2 inline-flex items-center gap-[7px] rounded-[8px] border border-[#E9E9E9] bg-white px-[8px] py-[3px]"
            :data-testid="`${testidPrefix}-feedback-controls`"
        >
            <span
                class="text-[14px] leading-[20px] text-gray-800"
                :data-testid="`${testidPrefix}-feedback-question`"
            >
                {{ $t(`${i18nNamespace}.feedbackQuestion`) }}
            </span>

            <div class="inline-flex items-center gap-[4px]">
                <button
                    :aria-label="$t(`${i18nNamespace}.feedbackThumbsUp`)"
                    :aria-pressed="feedback === DraftFeedback.ThumbsUp"
                    class="inline-flex size-[20px] items-center justify-center"
                    :data-testid="`${testidPrefix}-feedback-thumbs-up`"
                    type="button"
                    @click="selectThumbsUpFeedback"
                >
                    <icon
                        :class="
                            feedback === DraftFeedback.ThumbsUp
                                ? 'fill-green-200 text-green-550'
                                : 'fill-transparent text-gray-800'
                        "
                        :data-testid="`${testidPrefix}-feedback-thumbs-up-icon`"
                        height="14px"
                        name="regular/ai-feedback-thumbs-up"
                        width="14px"
                    />
                </button>
                <button
                    :aria-label="$t(`${i18nNamespace}.feedbackThumbsDown`)"
                    :aria-pressed="feedback === DraftFeedback.ThumbsDown"
                    class="inline-flex size-[20px] items-center justify-center"
                    :data-testid="`${testidPrefix}-feedback-thumbs-down`"
                    type="button"
                    @click="selectThumbsDownFeedback"
                >
                    <icon
                        :class="
                            feedback === DraftFeedback.ThumbsDown
                                ? 'fill-red-200 text-red-550'
                                : 'fill-transparent text-gray-800'
                        "
                        :data-testid="`${testidPrefix}-feedback-thumbs-down-icon`"
                        height="14px"
                        name="regular/ai-feedback-thumbs-down"
                        width="14px"
                    />
                </button>
            </div>
        </div>

        <ai-draft-feedback-panel
            :i18n-namespace="i18nNamespace"
            :testid-prefix="testidPrefix"
            @close="closeFeedbackPanel"
            @submit="submitFeedbackPanel"
            v-if="feedbackPanelOpened"
        />
    </div>
</template>

<script setup lang="ts">
    import { ref, watch } from 'vue';
    import AiDraftFeedbackPanel from '@/components/aiDraft/feedback/AiDraftFeedbackPanel.vue';
    import type { AiDraftFeedbackSubmission } from '@/components/aiDraft/feedback/types.ts';
    import { DraftFeedback } from '@/entities/enums/DraftFeedback.ts';

    const props = defineProps<{
        feedback: DraftFeedback | null;
        testidPrefix: string;
        i18nNamespace: string;
    }>();

    const emit = defineEmits<{
        feedbackClose: [];
        feedbackSubmit: [submission: AiDraftFeedbackSubmission];
        selectFeedback: [feedback: DraftFeedback];
        panelResize: [];
    }>();

    const feedbackPanelOpened = ref(props.feedback === DraftFeedback.ThumbsDown);

    watch(
        () => props.feedback,
        (feedback) => {
            feedbackPanelOpened.value = feedback === DraftFeedback.ThumbsDown;
        }
    );

    // Pre-flush: signal before the panel's v-if mounts/unmounts so the host reads pre-change geometry.
    watch(feedbackPanelOpened, () => {
        emit('panelResize');
    });

    const selectThumbsUpFeedback = () => {
        feedbackPanelOpened.value = false;
        emit('selectFeedback', DraftFeedback.ThumbsUp);
    };

    const selectThumbsDownFeedback = () => {
        emit('selectFeedback', DraftFeedback.ThumbsDown);
        feedbackPanelOpened.value = true;
    };

    const closeFeedbackPanel = () => {
        feedbackPanelOpened.value = false;
        emit('feedbackClose');
    };

    const submitFeedbackPanel = (submission: AiDraftFeedbackSubmission) => {
        feedbackPanelOpened.value = false;
        emit('feedbackSubmit', submission);
    };
</script>
